/**
 * HTTP client — all network primitives live here.
 * Nothing domain-specific; just fetch wrappers, error handling, and config.
 */
import { secureStorage } from '../secureStorage';
import { patientStorage } from '../patientStorage';
import { safeConsole, maskCredentials } from '../piiProtection';
import { getFriendlyErrorMessage } from '../errorMapping';
import { captureStaffSession, capturePatientSession } from './sessionToken';
import { csrfHeader, patientCsrfHeader } from '../csrf';
import i18n from '../../i18n';

// ─── Environment & Base URL ───────────────────────────────────────────────────

const APP_ENV =
  (import.meta.env.VITE_APP_ENV as string | undefined) ??
  (import.meta.env.DEV ? 'dev' : 'prod');

function resolveApiBaseUrl(): string {
  let envUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

  const hostname = window.location.hostname;
  const isIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);

  // When the browser is accessing via a LAN IP in Vite dev mode, the Vite proxy
  // (which forwards /api → localhost:8080) is the only reliable path to the local
  // backend. Route through window.location.origin so the proxy is used regardless
  // of what VITE_API_BASE_URL is set to.
  if (isIP && import.meta.env.DEV) {
    return window.location.origin;
  }

  if (isIP && envUrl?.includes('localhost')) {
    envUrl = envUrl.replace('localhost', hostname);
  }

  if (envUrl) {
    return envUrl.replace(/\/api\/?$/, '');
  }

  const env = APP_ENV.toLowerCase();
  if (env === 'localhost') return 'http://localhost:8080';
  if (env === 'dev' || env === 'development' || env === 'staging')
    return 'https://dev-api.clinvea.com';
  return 'https://api.clinvea.com';
}

export const API_BASE_URL = resolveApiBaseUrl();

safeConsole.log('🌐 API URL:', API_BASE_URL);
safeConsole.log('📦 VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
safeConsole.log('🎯 VITE_APP_ENV:', import.meta.env.VITE_APP_ENV);
if (import.meta.env.DEV) {
  safeConsole.log('📦 Environment:', APP_ENV);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  /** The server's machine-readable error code (e.g. VERIFICATION_EXPIRED), when it sent one. */
  errorCode?: string;
  /** The server's error body, for screens that act on its details (e.g. the records a duplicate check found). */
  errorDetails?: Record<string, unknown>;
  /** The HTTP status of a failed request, when the server answered (absent on a network failure). */
  status?: number;
  message?: string;
  verificationToken?: string;
  /**
   * Paging numbers a list envelope sent beside its rows ({ data, total, totalPages, page, pageSize }).
   * Unwrapping `data` would otherwise drop them, and a screen's pager would never appear.
   */
  total?: number;
  totalPages?: number;
  page?: number;
  pageSize?: number;
  /** Total rows, for envelopes that call it `count` (e.g. GET /admin/medications/all). */
  count?: number;
}

const PAGING_KEYS = ['total', 'totalPages', 'page', 'pageSize', 'count'] as const;

/**
 * Turns a parsed success body into an ApiResponse: an envelope { data, ... } yields its data, and any
 * numeric paging numbers beside it (total, totalPages, page, pageSize) are kept on the response.
 * Any other body is returned whole as data.
 */
export function unwrapEnvelope<T>(body: unknown): ApiResponse<T> {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) {
    const env = body as Record<string, unknown>;
    const out: ApiResponse<T> = { data: env.data as T };
    for (const key of PAGING_KEYS) {
      if (typeof env[key] === 'number') out[key] = env[key] as number;
    }
    return out;
  }
  return { data: body as T };
}

export class ApiError extends Error {
  constructor(message: string, public status?: number, public code?: string, public details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown (not returned) when the server responds 429. Carries the Retry-After seconds. */
export class RateLimitError extends ApiError {
  constructor(public readonly retryAfterSeconds: number) {
    super('Too many requests', 429);
    this.name = 'RateLimitError';
  }
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function extractFileName(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined;
  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1];
}

function baseHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    ...extra,
  };
}

// ─── Silent-refresh-and-retry-once on 401 ──────────────────────────────────────
//
// A 401 can mean "genuinely logged out" or just "access token expired between the proactive
// background refresh timer (AuthContext/PatientAuthContext) and this particular request." The
// latter is common right around the token's natural expiry — rather than bouncing the user to
// /login for what's actually a recoverable, momentary gap, attempt exactly one silent refresh and
// retry. Concurrent 401s share a single in-flight refresh call (via the module-level promises
// below) instead of each firing their own — refresh-token rotation is single-use, so N simultaneous
// refresh attempts would have N-1 of them fail as "already rotated."
//
// Deliberately implemented as raw fetch() calls here rather than importing authModule/
// patientPortalModule's refresh() — those modules import fetchApi/fetchPatientApi from this file,
// so importing them back here would be a circular import.

let staffRefreshInFlight: Promise<boolean> | null = null;
let patientRefreshInFlight: Promise<boolean> | null = null;

async function attemptStaffRefresh(): Promise<boolean> {
  if (!staffRefreshInFlight) {
    staffRefreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: baseHeaders(),
          credentials: 'include',
        });
        if (!response.ok) return false;
        const body = await response.json().catch(() => null);
        // Raw fetch, so `body` is the backend response itself rather than a fetchApi envelope.
        // Wrapping it in { data } lines the shapes up so this shares one extraction with the rest.
        return captureStaffSession({ data: body }) !== null;
      } catch {
        return false;
      } finally {
        staffRefreshInFlight = null;
      }
    })();
  }
  return staffRefreshInFlight;
}

async function attemptPatientRefresh(): Promise<boolean> {
  if (!patientRefreshInFlight) {
    patientRefreshInFlight = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/patient/auth/refresh`, {
          method: 'POST',
          headers: { ...baseHeaders(), [PATIENT_TRANSPORT_HEADER]: 'cookie' },
          credentials: 'include',
        });
        if (!response.ok) return false;
        const body = await response.json().catch(() => null);
        // The new token arrived as an HttpOnly cookie; the body only says how long it lasts (medopd-docs 107).
        return capturePatientSession({ data: body ?? { expiresInSeconds: 0 } }) !== null;
      } catch {
        return false;
      } finally {
        patientRefreshInFlight = null;
      }
    })();
  }
  return patientRefreshInFlight;
}

/**
 * The headers every staff API call carries: JSON content type, request id, CSRF on state-changing methods (the
 * cookie-authenticated browser path must echo XSRF-TOKEN or CsrfProtectionFilter refuses the write), the session
 * token, the active clinic (TenantContextFilter) and the active pharmacy. Exported for callers that need a raw
 * fetch (the Copilot's streamed answer cannot go through fetchApi's JSON parsing).
 */
export function staffRequestHeaders(
  method?: string,
  requestId: string = generateRequestId(),
  extra?: Record<string, string>,
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': requestId,
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    ...csrfHeader(method),
    ...extra,
  };

  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken) headers['X-Session-Token'] = sessionToken;

  // Multi-branch: attach active clinic header if set.
  // TenantContextFilter on the backend validates this per-request.
  const activeClinicId = sessionStorage.getItem('active_clinic_id');
  if (activeClinicId) headers['X-Active-Clinic-Id'] = activeClinicId;

  // Multi-pharmacy accounts: the pharmacy picked in the switcher (useActivePharmacy). The server checks the link.
  const activePharmacyId = sessionStorage.getItem('active_pharmacy_id');
  if (activePharmacyId) headers['X-Active-Pharmacy-Id'] = activePharmacyId;
  return headers;
}

/** One shared silent refresh of the staff session (see attemptStaffRefresh), for raw-fetch callers. */
export function refreshStaffSession(): Promise<boolean> {
  return attemptStaffRefresh();
}

// ─── Core fetch wrappers ──────────────────────────────────────────────────────

export async function fetchApi<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean; skipBodyLog?: boolean; _retried?: boolean } = {}
): Promise<ApiResponse<T>> {
  const requestId = generateRequestId();

  const headers = staffRequestHeaders(options.method, requestId, options.headers as Record<string, string> | undefined);

  if (options.body && !options.skipBodyLog) {
    let parsed: unknown;
    try { parsed = JSON.parse(String(options.body)); } catch { parsed = String(options.body); }
    safeConsole.log(
      `[API Request ${requestId}] ${options.method ?? 'GET'} ${endpoint}`,
      { body: maskCredentials(parsed) }
    );
  }

  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        const isLoginEndpoint   = endpoint === '/api/auth/login';
        const isLogoutEndpoint  = endpoint === '/api/auth/logout';
        const isRefreshEndpoint = endpoint === '/api/auth/refresh';

        // One silent refresh-and-retry attempt before falling back to the redirect-to-login path
        // below. Never attempted for the auth endpoints themselves (would loop) or a request that's
        // already a retry (bounds this to exactly one attempt per original call).
        if (!isLoginEndpoint && !isLogoutEndpoint && !isRefreshEndpoint && !options._retried) {
          const refreshed = await attemptStaffRefresh();
          if (refreshed) {
            return fetchApi<T>(endpoint, { ...options, _retried: true });
          }
        }

        const isRecentAuth      = secureStorage.isRecentLogin();
        const isReadOnly        = (options?.method ?? 'GET') === 'GET';

        if (!isLoginEndpoint && !isLogoutEndpoint && !isRecentAuth && !isReadOnly) {
          secureStorage.removeAuthToken();
          setTimeout(() => { window.location.href = '/login'; }, 100);
        }

        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || errorData.error || 'Unauthorized',
          401
        );
      }

      const errorData = await response.json().catch(() => ({}));
      safeConsole.error(`[API Error ${requestId}]`, response.status, errorData);

      if (response.status === 429) {
        const headerVal = response.headers.get('Retry-After');
        const raw = parseInt(headerVal ?? String(errorData.retryAfter ?? 60), 10);
        throw new RateLimitError(isNaN(raw) ? 60 : raw);
      }

      // An admin who must use two-step verification and hasn't set it up can only do that (medopd-docs 96).
      if (response.status === 403 && errorData.errorCode === 'MFA_ENROLLMENT_REQUIRED'
          && typeof window !== 'undefined' && window.location.pathname !== '/security/setup-mfa') {
        window.location.assign('/security/setup-mfa');
      }

      throw new ApiError(
        errorData.message || errorData.error || 'An error occurred',
        response.status,
        typeof errorData.code === 'string' ? errorData.code : typeof errorData.errorCode === 'string' ? errorData.errorCode : undefined,
        response.status === 409 && errorData && typeof errorData === 'object' ? errorData : undefined
      );
    }

    // 204 No Content (e.g. a DELETE) has no body to parse: it is still a success.
    if (response.status === 204 || response.headers.get('content-length') === '0') return { data: undefined as T };
    const responseData = await response.json();
    safeConsole.log(`[API Response ${requestId}] Success`);

    return unwrapEnvelope<T>(responseData);
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    if (error instanceof ApiError) {
      return {
        error: getFriendlyErrorMessage(error.message, error.code),
        ...(error.code ? { errorCode: error.code } : {}),
        ...(error.details ? { errorDetails: error.details } : {}),
        ...(error.status ? { status: error.status } : {}),
      };
    }
    safeConsole.error(`[API Network Error ${requestId}]`, error);
    return { error: 'Network error. Please check your connection.' };
  }
}

// ── In-flight counter ────────────────────────────────────────────────────────────────────────
// How many API calls are running right now, for global "working" indicators (the public care pages'
// BusyBar). Kept here so a direct call from a step's handler counts the same as a query or mutation.
let inFlight = 0;
const activityListeners = new Set<() => void>();
function notifyActivity() { activityListeners.forEach((l) => l()); }
function trackedFetch(input: string, init?: RequestInit): Promise<Response> {
  inFlight += 1;
  notifyActivity();
  return fetch(input, init).finally(() => { inFlight = Math.max(0, inFlight - 1); notifyActivity(); });
}
export const apiActivity = {
  subscribe(listener: () => void): () => void {
    activityListeners.add(listener);
    return () => { activityListeners.delete(listener); };
  },
  inFlight: (): number => inFlight,
};

/**
 * Asks the server to keep the patient session in HttpOnly cookies rather than return the token (medopd-docs 107).
 * Sent on every patient request: harmless where no session is issued.
 */
const PATIENT_TRANSPORT_HEADER = 'X-Auth-Transport';

/** Ends a session that sat idle past the timeout: the cookie would otherwise still be accepted. */
function endIdlePatientSession(): void {
  patientStorage.clearSession();
  fetch(`${API_BASE_URL}/api/patient/auth/logout`, {
    method: 'POST', headers: baseHeaders(), credentials: 'include', keepalive: true,
  }).catch(() => undefined);
  window.location.href = '/patient/login';
}

/**
 * Headers for a raw patient fetch that cannot go through fetchPatientApi (the Ask ClinVea assistant streams its answer
 * and reads the server's error codes itself): JSON, request id, the cookie transport, the patient CSRF echo on writes
 * and the active linked clinic. Never the family-member header: the assistant always acts as the signed-in person and
 * the server refuses it in the family-member view.
 */
export function patientRequestHeaders(method?: string, extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    [PATIENT_TRANSPORT_HEADER]: 'cookie',
    ...patientCsrfHeader(method),
    ...extra,
  };
  const activePatientClinicId = sessionStorage.getItem('active_patient_clinic_id');
  if (activePatientClinicId && !headers['X-Active-Patient-Clinic-Id']) headers['X-Active-Patient-Clinic-Id'] = activePatientClinicId;
  return headers;
}

/** One shared silent refresh of the patient session (see attemptPatientRefresh), for raw-fetch callers. */
export function refreshPatientSession(): Promise<boolean> {
  return attemptPatientRefresh();
}

export async function fetchPatientApi<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean; _retried?: boolean } = {}
): Promise<ApiResponse<T>> {
  if (!options.skipAuth && patientStorage.isIdleExpired() && !endpoint.startsWith('/api/patient/auth/')) {
    endIdlePatientSession();
    return { error: i18n.t('generic.sessionExpired', { ns: 'errors', defaultValue: 'Your session has ended. Please sign in again.' }) };
  }

  // No Authorization header: the session is the HttpOnly patient_auth_token cookie (credentials: 'include').
  const headers: Record<string, string> = {
    // A FormData body (file upload) must let the browser set multipart/form-data with its boundary.
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
    [PATIENT_TRANSPORT_HEADER]: 'cookie',
    ...patientCsrfHeader(options.method),
    ...(options.headers as Record<string, string> | undefined),
  };

  // Which linked clinic the patient is currently viewing — defaults to their primary clinic (the JWT
  // claim) when unset. See PatientTenantContextFilter on the backend.
  // A call may name its own linked clinic (an action on an appointment row from another linked clinic, medopd-docs 137):
  // that wins for this one request; the server still accepts it only for a clinic linked to this login.
  const activePatientClinicId = sessionStorage.getItem('active_patient_clinic_id');
  if (activePatientClinicId && !headers['X-Active-Patient-Clinic-Id']) headers['X-Active-Patient-Clinic-Id'] = activePatientClinicId;

  // Which linked dependent (child/parent/spouse) the account holder is currently viewing, if any.
  // See PatientDependentContextFilter on the backend — unlike the clinic header above, an invalid/
  // revoked/aged-out id here FAILS CLOSED (403), never silently falls back to the account holder's
  // own records.
  const activeDependentPatientId = sessionStorage.getItem('active_dependent_patient_id');
  // Not sent for the family list itself, nor for the login's own screen settings (page layout — always the login's,
  // whoever is on screen; an older server would refuse that save as a dependent write).
  const dependentContextActive = Boolean(activeDependentPatientId) && !endpoint.startsWith('/api/patient/dependents')
    && !endpoint.startsWith('/api/patient/me/ui-settings/');
  if (dependentContextActive) headers['X-Active-Dependent-Patient-Id'] = activeDependentPatientId as string;

  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        const isLoginEndpoint = endpoint === '/api/patient/auth/login';
        const isRefreshEndpoint = endpoint === '/api/patient/auth/refresh';
        const isLogoutEndpoint = endpoint === '/api/patient/auth/logout';

        if (!isLoginEndpoint && !isRefreshEndpoint && !isLogoutEndpoint && !options._retried) {
          const refreshed = await attemptPatientRefresh();
          if (refreshed) {
            return fetchPatientApi<T>(endpoint, { ...options, _retried: true });
          }
        }

        if (!isLoginEndpoint) {
          patientStorage.clearSession();
          window.location.href = '/patient/login';
        }
        throw new ApiError('Unauthorized', 401);
      }
      // A change refused because the dependent view is read-only (medopd-docs 96) is NOT a dead
      // context: keep the dependent selected and show the server's message instead.
      const readOnlyRefusal = response.status === 403 && dependentContextActive
        && (await response.clone().json().catch(() => ({})))?.code === 'DEPENDENT_READ_ONLY';
      if (response.status === 403 && dependentContextActive && !readOnlyRefusal) {
        // Fail-closed denial: the switched dependent context is no longer valid (revoked, aged
        // out, unlinked from another tab). Clear it and send the user back to a safe screen
        // rather than let every subsequent call under this tab keep 403ing silently.
        sessionStorage.removeItem('active_dependent_patient_id');
        window.location.href = '/patient/family';
        throw new ApiError('Dependent access no longer valid', 403);
      }
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 429) {
        const headerVal = response.headers.get('Retry-After');
        const raw = parseInt(headerVal ?? String(errorData.retryAfter ?? 60), 10);
        throw new RateLimitError(isNaN(raw) ? 60 : raw);
      }

      // An admin who must use two-step verification and hasn't set it up can only do that (medopd-docs 96).
      if (response.status === 403 && errorData.errorCode === 'MFA_ENROLLMENT_REQUIRED'
          && typeof window !== 'undefined' && window.location.pathname !== '/security/setup-mfa') {
        window.location.assign('/security/setup-mfa');
      }

      throw new ApiError(
        errorData.message || errorData.error || 'An error occurred',
        response.status,
        typeof errorData.code === 'string' ? errorData.code : typeof errorData.errorCode === 'string' ? errorData.errorCode : undefined
      );
    }

    patientStorage.touch(); // activity slides the inactivity timeout
    // 204 No Content (e.g. a DELETE) has no body to parse: it is still a success.
    if (response.status === 204 || response.headers.get('content-length') === '0') return { data: undefined as T };
    const responseData = await response.json();
    return unwrapEnvelope<T>(responseData);
  } catch (error) {
    if (error instanceof RateLimitError) throw error;
    if (error instanceof ApiError) {
      // status lets callers tell "this server has no such endpoint" (404) from a refusal — as fetchApi does.
      return { error: getFriendlyErrorMessage(error.message, error.code), ...(error.code ? { errorCode: error.code } : {}), ...(error.status ? { status: error.status } : {}) };
    }
    return { error: i18n.t('generic.network', { ns: 'errors', defaultValue: 'Cannot reach the server. Check your internet connection and try again.' }) };
  }
}

/** POST to an endpoint, download the response as a Blob. */
export async function fetchFileApi(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<{ blob: Blob; fileName?: string; headers: Headers }>> {
  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...baseHeaders(), ...csrfHeader('POST') },
      body: JSON.stringify(body),
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new ApiError('Unauthorized', 401);
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError(
        (err as any).message || (err as any).error || 'An error occurred',
        response.status
      );
    }

    const blob = await response.blob();
    return {
      data: {
        blob,
        fileName: extractFileName(response.headers.get('content-disposition')),
        // Callers read outcome headers (e.g. X-Email-Status on vitals export); cross-origin they
        // must also be listed in the backend's CORS exposedHeaders.
        headers: response.headers,
      },
    };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/**
 * POST multipart/form-data — does NOT set Content-Type so the browser
 * automatically adds the boundary. Includes all standard auth headers.
 */
export async function fetchMultipartApi<T>(
  endpoint: string,
  body: FormData
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'X-Request-ID': generateRequestId(),
    'X-Client-Version': '1.0.0',
    'X-App-Environment': APP_ENV,
  };

  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken) headers['X-Session-Token'] = sessionToken;

  const activeClinicId = sessionStorage.getItem('active_clinic_id');
  if (activeClinicId) headers['X-Active-Clinic-Id'] = activeClinicId;
  const activePharmacyId = sessionStorage.getItem('active_pharmacy_id');
  if (activePharmacyId) headers['X-Active-Pharmacy-Id'] = activePharmacyId;

  Object.assign(headers, csrfHeader('POST')); // CSRF token for the cookie-authenticated browser path

  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new ApiError('Unauthorized', 401);
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError(
        (err as any).message || (err as any).error || 'An error occurred',
        response.status
      );
    }

    const data = await response.json().catch(() => undefined);
    // Same envelope handling as fetchApi: an ApiResponse {success, data} yields its data, so callers
    // get the same shape whichever helper they use (the raw envelope here once crashed a page that
    // cached it as the resource).
    if (data && typeof data === 'object' && 'data' in data) {
      return { data: (data as { data: T }).data };
    }
    return { data: data as T };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/** POST to a public/patient endpoint (no staff cookie), download the response as a Blob. */
export async function fetchPatientPostFileApi(
  endpoint: string,
  body: unknown
): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      // The patient session is the HttpOnly cookie (medopd-docs 107); writes echo the patient CSRF token.
      headers: { 'Content-Type': 'application/json', ...patientCsrfHeader('POST'), 'X-Request-ID': generateRequestId() },
      body: JSON.stringify(body),
      credentials: 'include',
    });
    if (!response.ok) {
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      const apiErr = new ApiError((err as any).message || (err as any).error || 'An error occurred', response.status);
      const code = (err as any).code ?? (err as any).errorCode;
      if (code) return { error: getFriendlyErrorMessage(apiErr.message), errorCode: String(code) };
      throw apiErr;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    return { data: { blob, fileName: match?.[1] } };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/** GET a patient-portal endpoint (patient JWT), download the response as a Blob. */
export async function fetchPatientGetFileApi(
  endpoint: string
): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: { 'X-Request-ID': generateRequestId() }, // the session is the HttpOnly cookie (medopd-docs 107)
      credentials: 'include',
    });
    if (!response.ok) {
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError((err as any).message || (err as any).error || 'An error occurred', response.status);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    return { data: { blob, fileName: match?.[1] } };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/** GET an endpoint, download the response as a Blob. */
export async function fetchGetFileApi(
  endpoint: string
): Promise<ApiResponse<{ blob: Blob; fileName?: string }>> {
  try {
    const response = await trackedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: baseHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) throw new ApiError('Unauthorized', 401);
      const isJson = response.headers.get('content-type')?.includes('application/json');
      const err = isJson ? await response.json().catch(() => ({})) : {};
      throw new ApiError(
        (err as any).message || (err as any).error || 'An error occurred',
        response.status
      );
    }

    const blob = await response.blob();
    return { data: { blob, fileName: extractFileName(response.headers.get('content-disposition')) } };
  } catch (error) {
    if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
    return { error: 'Network error. Please check your connection.' };
  }
}

/**
 * fetchApi resolves with `{ error }` instead of throwing, so a mutation that only awaits it treats a
 * failed request as success (the "false success" bug class). Wrap mutation calls in this to get a
 * real rejection carrying the server's message.
 */
export async function orThrow<T>(request: Promise<ApiResponse<T>>): Promise<T | undefined> {
  const res = await request;
  if (res.error) {
    // Keep the server's code on the thrown error so a screen can act on it (e.g. IDENTITY_NOT_VERIFIED
    // sends the check-in page back to the verification step) — the message alone is translated text.
    throw Object.assign(new Error(res.error), res.errorCode ? { code: res.errorCode } : {});
  }
  return res.data;
}
